/**
 * Bilingual route configuration for Colegio ARCE.
 * Maps ES/EN slugs to components and provides SEO metadata.
 */

// Route definitions: [esSlug, enSlug, componentName, seoES, seoEN]
export const ROUTE_MAP = [
  {
    es: '/', en: '/', component: 'LandingPage',
    seo: {
      es: { title: 'Colegio ARCE — Clases de Español para Niños', description: 'Clases virtuales de español para niños y jóvenes. Grupos reducidos, profesores nativos con más de 15 años de experiencia. Medford, MA.' },
      en: { title: 'Colegio ARCE — Spanish Classes for Kids', description: 'Online Spanish classes for children and teens. Small groups, native teachers with 15+ years of experience. Medford, MA.' },
    }
  },
  {
    es: '/quienes-somos', en: '/about', component: 'AboutPage',
    seo: {
      es: { title: 'Quiénes Somos | Colegio ARCE', description: 'Conoce al equipo de Colegio ARCE. Profesores nativos certificados con más de 15 años de experiencia enseñando español a niños.' },
      en: { title: 'About Us | Colegio ARCE', description: 'Meet the Colegio ARCE team. Certified native teachers with 15+ years of experience teaching Spanish to children.' },
    }
  },
  {
    es: '/metodologia', en: '/methodology', component: 'MethodologyPage',
    seo: {
      es: { title: 'Metodología | Colegio ARCE', description: 'Nuestra metodología sigue el currículo oficial del Ministerio de Educación de España, adaptado para estudiantes internacionales.' },
      en: { title: 'Methodology | Colegio ARCE', description: 'Our methodology follows the official curriculum of Spain\'s Ministry of Education, adapted for international students.' },
    }
  },
  {
    es: '/filosofia', en: '/philosophy', component: 'PhilosophyPage',
    seo: {
      es: { title: 'Filosofía | Colegio ARCE', description: 'Creemos en el aprendizaje inmersivo y el arraigo cultural como pilares del dominio del español para niños y jóvenes.' },
      en: { title: 'Philosophy | Colegio ARCE', description: 'We believe in immersive learning and cultural roots as pillars of Spanish mastery for children and teens.' },
    }
  },
  {
    es: '/programas', en: '/programs', component: 'CoursesPage',
    seo: {
      es: { title: 'Programas y Niveles | Colegio ARCE', description: 'Clases semanales de 50 minutos organizadas por edad y nivel. De Parvulitos a Bachillerato, incluido preparación DELE.' },
      en: { title: 'Programs & Levels | Colegio ARCE', description: 'Weekly 50-minute classes organized by age and level. From Preschool to High School, including DELE preparation.' },
    }
  },
  {
    es: '/calendario', en: '/calendar', component: 'CalendarPage',
    seo: {
      es: { title: 'Calendario Escolar | Colegio ARCE', description: 'Calendario del año escolar 2026-2027. Tres trimestres de septiembre a junio, 30 semanas de clases.' },
      en: { title: 'School Calendar | Colegio ARCE', description: 'Academic year 2026-2027 calendar. Three trimesters from September to June, 30 weeks of classes.' },
    }
  },
  {
    es: '/tarifas', en: '/pricing', component: 'PricingPage',
    seo: {
      es: { title: 'Tarifas | Colegio ARCE', description: 'Planes de inscripción: anual desde $1,495 o trimestral a $620. Descuento de hermanos 20%. Pago por Zelle.' },
      en: { title: 'Pricing | Colegio ARCE', description: 'Enrollment plans: annual from $1,495 or trimester at $620. 20% sibling discount. Payment via Zelle.' },
    }
  },
  {
    es: '/contacto', en: '/contact', component: 'ContactPage',
    seo: {
      es: { title: 'Contacto | Colegio ARCE', description: 'Contáctanos para más información sobre nuestras clases de español. WhatsApp, email o formulario de contacto.' },
      en: { title: 'Contact | Colegio ARCE', description: 'Contact us for more information about our Spanish classes. WhatsApp, email, or contact form.' },
    }
  },
  {
    es: '/inscripciones', en: '/enrollment', component: 'EnrollmentLandingPage',
    seo: {
      es: { title: 'Inscripciones | Colegio ARCE', description: 'Inscribe a tu hijo en Colegio ARCE. Evaluación de nivel, elección de plan y pago seguro por Zelle.' },
      en: { title: 'Enrollment | Colegio ARCE', description: 'Enroll your child at Colegio ARCE. Level assessment, plan selection, and secure Zelle payment.' },
    }
  },
  {
    es: '/inscripciones/nueva', en: '/enrollment/new', component: 'NewEnrollmentPage',
    seo: {
      es: { title: 'Nueva Inscripción | Colegio ARCE', description: 'Evaluación de nivel para nuevos alumnos. $52 deducibles de la matrícula.' },
      en: { title: 'New Enrollment | Colegio ARCE', description: 'Level assessment for new students. $52 deductible from tuition.' },
    }
  },
  {
    es: '/inscripciones/reinscripcion', en: '/enrollment/reenroll', component: 'EnrollmentPage',
    seo: {
      es: { title: 'Reinscripción | Colegio ARCE', description: 'Reinscripción para alumnos que regresan. Pago único o trimestral con cupón de descuento.' },
      en: { title: 'Re-enrollment | Colegio ARCE', description: 'Re-enrollment for returning students. Full or trimester payment with discount coupon.' },
    }
  },
  {
    es: '/blog', en: '/blog', component: 'BlogPage',
    seo: {
      es: { title: 'Blog | Colegio ARCE', description: 'Artículos sobre educación bilingüe, cultura hispana y consejos para padres de familia.' },
      en: { title: 'Blog | Colegio ARCE', description: 'Articles about bilingual education, Hispanic culture, and tips for parents.' },
    }
  },
  {
    es: '/equipo', en: '/team', component: 'TeamPage',
    seo: {
      es: { title: 'Equipo | Colegio ARCE', description: 'Nuestro equipo de profesores nativos certificados y personal administrativo.' },
      en: { title: 'Team | Colegio ARCE', description: 'Our team of certified native teachers and administrative staff.' },
    }
  },
  {
    es: '/preguntas-frecuentes', en: '/faq', component: 'FAQPage',
    seo: {
      es: { title: 'Preguntas Frecuentes | Colegio ARCE', description: 'Respuestas a las preguntas más frecuentes sobre inscripción, horarios, pagos y metodología.' },
      en: { title: 'FAQ | Colegio ARCE', description: 'Answers to frequently asked questions about enrollment, schedules, payments, and methodology.' },
    }
  },
  {
    es: '/privacidad', en: '/privacy', component: 'PrivacyPage',
    seo: {
      es: { title: 'Política de Privacidad | Colegio ARCE', description: 'Política de privacidad y protección de datos de Colegio ARCE.' },
      en: { title: 'Privacy Policy | Colegio ARCE', description: 'Privacy policy and data protection at Colegio ARCE.' },
    }
  },
  {
    es: '/terminos', en: '/terms', component: 'TermsPage',
    seo: {
      es: { title: 'Términos y Condiciones | Colegio ARCE', description: 'Términos y condiciones de uso de Colegio ARCE.' },
      en: { title: 'Terms & Conditions | Colegio ARCE', description: 'Terms and conditions of use at Colegio ARCE.' },
    }
  },
];

/**
 * Get the equivalent path in the other language.
 * E.g. getAlternatePath('/es/tarifas', 'en') → '/en/pricing'
 */
export function getAlternatePath(currentPath, targetLang) {
  // Remove language prefix
  const cleanPath = currentPath.replace(/^\/(es|en)/, '') || '/';
  const sourceLang = currentPath.startsWith('/en') ? 'en' : 'es';
  
  for (const route of ROUTE_MAP) {
    if (route[sourceLang] === cleanPath) {
      return `/${targetLang}${route[targetLang] === '/' ? '' : route[targetLang]}`;
    }
  }
  // Blog post special case
  if (cleanPath.startsWith('/blog/')) {
    return `/${targetLang}${cleanPath}`;
  }
  // Default: just swap prefix
  return `/${targetLang}${cleanPath}`;
}

/**
 * Get SEO data for a given path.
 */
export function getSeoForPath(path) {
  const lang = path.startsWith('/en') ? 'en' : 'es';
  const cleanPath = path.replace(/^\/(es|en)/, '') || '/';
  
  for (const route of ROUTE_MAP) {
    if (route[lang] === cleanPath) {
      const seo = route.seo[lang];
      const altLang = lang === 'es' ? 'en' : 'es';
      const altPath = route[altLang] === '/' ? '' : route[altLang];
      return {
        ...seo,
        lang,
        canonical: `https://www.colegioarce.com/${lang}${cleanPath === '/' ? '' : cleanPath}`,
        hreflang: {
          es: `https://www.colegioarce.com/es${route.es === '/' ? '' : route.es}`,
          en: `https://www.colegioarce.com/en${route.en === '/' ? '' : route.en}`,
          'x-default': `https://www.colegioarce.com/es${route.es === '/' ? '' : route.es}`,
        }
      };
    }
  }
  return null;
}

/**
 * Old Squarespace URLs → new URLs (for 301 redirects)
 */
export const LEGACY_REDIRECTS = {
  '/inicio': '/es/quienes-somos',
  '/acerca-de-nosotros': '/es/filosofia',
  '/acerca-de-nosotros-1': '/es/quienes-somos',
  '/que-ofrecemos': '/es/programas',
  '/calendario-20252026': '/es/calendario',
  // Old English-ish paths
  '/pricing': '/es/tarifas',
  '/contact': '/es/contacto',
  '/courses': '/es/programas',
  '/about': '/es/quienes-somos',
  '/team': '/es/equipo',
  '/enrollment': '/es/inscripciones',
};
